<template>
    <custom-swiper class="cz2__product-recs">
        <template v-slot:slides>
            <div
                v-for="product in products"
                :key="product.uuid"
                @click="showProductInfoModal(product)"
                class="swiper-slide slide-group cz2__checkout__sticker__slide"
            >
                <div>
                    <div
                        class="cz2__checkout__sticker__slide__image"
                        :style="{ backgroundImage: `url(&quot;${getImage(product)}&quot;)` }"
                    />

                    <div class="cz2__checkout__sticker__slide__description">
                        <h3 class="cz2__checkout__sticker__slide__heading limit-2-lines">
                            {{ product.license }}
                        </h3>

                        <h4 class="cz2__checkout__sticker__slide__title limit-3-lines">
                            {{ product.productName }}
                        </h4>
                    </div>
                </div>

                <div class="cz2__checkout__sticker__slide__price">
                    <div class="cz2__checkout__sticker__slide__heading">
                        {{ product.price.sales.formatted }}
                    </div>

                    <span class="cz2__sr-only">
                        {{ $t('SR_ACTIVATING') }}
                    </span>

                    <btn
                        v-if="!product.selected"
                        @click.native.stop="addProduct(product)"
                        class="cz2__checkout__sticker__slide__button swiper-button cz2__stationary-text-transform"
                    >
                        {{ $t('ACTION_ADD_TO_CART') }}
                    </btn>

                    <div
                        v-else
                        @click.stop
                        class="cz2__quantity cz2__font-inter-black"
                    >
                        <button
                            class="cz2__quantity__sub"
                            @click.stop="quantitySubtract(product)"
                            :aria-label="product.quantity ? $t('QUANTITY_MINUS') : $t('QUANTITY_REMOVE')"
                            type="button"
                            v-html="$t('QUANTITY_MINUS_SYMBOL')"
                        />

                        <span class="cz2__quantity__count">
                            {{ product.quantity }}
                        </span>
                        <span class="cz2__sr-only">
                            {{ getProductDescription(product.productName) }}
                        </span>

                        <button
                            class="cz2__quantity__add"
                            :disabled="!product.canAdd"
                            @click.stop="quantityAdd(product)"
                            :aria-label="$t('QUANTITY_PLUS')"
                            type="button"
                            v-html="$t('QUANTITY_PLUS_SYMBOL')"
                        />
                    </div>
                </div>

                <div class="cz2__product-card-flag">
                    <product-flag />
                </div>
            </div>
        </template>
    </custom-swiper>
</template>

<style lang="scss">
    @import "../style/variables.scss";
    @import "../style/shared.scss";

    #cz2.cz2 .cz2__checkout__carousel {
        .cz2__product-recs {
            .cz2__checkout__sticker__slide {
                cursor: pointer;
                justify-content: space-between;

                .cz2__product-card-flag {
                    position: absolute;
                    top: 48px;
                    left: -35px;
                    transform: rotate(90deg);
                }
            }

            .limit-2-lines {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                // Max-height fallback for browsers that don't support line-clamp.
                max-height: 36px;
                min-height: 36px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .limit-3-lines {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 3;
                // Max-height fallback for browsers that don't support line-clamp.
                max-height: 54px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .cz2__checkout__sticker__slide__title {
                text-transform: none;
            }

            .cz2__checkout__sticker__slide__title,
            .cz2__checkout__sticker__slide__heading {
                line-height: 18px;
            }

            .cz2__checkout__sticker__slide__title {
                margin-bottom: 8px;
            }

            .cz2__add {
                position: relative;

                color: color(primaryWhite);

                &:focus {
                    border-color: color(secondaryBlack);
                }

                &:disabled {
                    opacity: 0.5;

                    cursor: not-allowed;
                }
            }

            .cz2__quantity {
                display: flex;
                align-items: center;
            }

            .cz2__add,
            .cz2__quantity {
                margin-top: 12px;
                border: 3px solid color(primaryWhite);
                border-radius: 33px;
                padding: 0 16px;
                width: 100%;
                font-style: italic;
            }

            .cz2__add,
            .cz2__quantity,
            .cz2__quantity__sub,
            .cz2__quantity__count,
            .cz2__quantity__add {
                text-align: center;
                font-size: 24px;
                letter-spacing: -0.89px;
            }

            .cz2__quantity__sub,
            .cz2__quantity__count,
            .cz2__quantity__add {
                color: white;
            }

            .cz2__quantity__count {
                font-size: 20px;
            }

            .cz2__quantity {
                border: 3px solid black;

                background: black;
                height: 40px;

                display: flex;
                justify-content: space-between;
            }

            .cz2__quantity__sub:disabled,
            .cz2__quantity__add:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }
    }
</style>

<script>
    import Btn from './ui/Btn.vue';
    import CustomSwiper from './ui/CustomSwiper.vue';
    import ProductFlag from './ui/ProductFlag.vue';

    import * as constants from '../constants';

    export default {
        components: {
            Btn,
            CustomSwiper,
            ProductFlag,
        },

        data() {
            return {
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            cart() {
                return this.$store.getters.cart;
            },

            selectedRecommendations() {
                return this.$store.state.customizer.selectedRecommendations;
            },

            recommendations() {
                return this.$store.state.customizer.recommendations;
            },

            products() {
                const list = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const product of this.recommendations) {
                    const record = product;

                    const selected = this.findSelected(record.id);

                    if (selected) {
                        record.selected = true;
                        record.quantity = selected.quantity;
                    } else {
                        record.selected = false;
                        record.quantity = 0;
                    }

                    record.inventory = this.quantityLimit(record.ats);
                    record.canAdd = record.quantity < record.inventory;

                    if (record.ats > 0) {
                        list.push(record);
                    }
                }

                return list;
            },
        },

        mounted() {
            // Read initial quantities.
            if (this.recommendations) {
                if (this.selectedRecommendations == null) {
                    const selected = [];

                    // eslint-disable-next-line no-restricted-syntax
                    for (const product of this.recommendations) {
                        if (product.inCartQuantityObj && product.inCartQuantityObj.inCartQuantity > 0) {
                            let price = null;

                            if (product.price?.sales?.value) {
                                price = product.price.sales.value;
                            }

                            selected.push({
                                id: product.id,
                                quantity: product.inCartQuantityObj.inCartQuantity,
                                itemUuid: product.inCartQuantityObj.itemUUID,
                                image: this.getImage(product),
                                name: product.productName,
                                price: price ? price.toString() : '',
                            });
                        }
                    }

                    this.$store.dispatch('setSelectedRecommendations', selected);
                }
            }
        },

        methods: {
            addProduct(product) {
                this.$store.dispatch('log', {
                    level: 'info',
                    message: 'Add product',
                });

                this.$store.dispatch('log', {
                    level: 'info',
                    message: `Select product ${product.id}`,
                });

                const selected = this.selectedRecommendations.find((x) => x.id === product.id);
                if (selected == null) {
                    let price = null;

                    if (product.price?.sales?.value) {
                        price = product.price.sales.value;
                    }

                    const add = {
                        id: product.id,
                        quantity: 1,
                        image: this.getImage(product),
                        name: product.productName,
                        price: price ? price.toString() : '',
                    };

                    this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations, add]);

                    // this.addProductToCart(product);
                } else {
                    const add = { ...selected, quantity: 1 };

                    this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations.filter((x) => x.id !== product.id), add]);

                    // this.addProductToCart(product);
                }
            },

            addProductToCart(product) {
                for (let i = 1; i < 20; i += 1) {
                    // Find empty placement.
                    const placementCode = `${constants.PLACEMENT_PRODUCTS}${i}`;
                    const selected = this.viewController.selectedAtPlacement(placementCode);

                    if (selected?.placeholder) {
                        const placement = this.viewController.findPlacement(placementCode);
                        // Use this one.
                        const realProduct = placement.components.find((x) => !x.placeholder);

                        this.viewController.updateComponent(placementCode, realProduct.code);

                        let price = null;

                        if (product.price?.sales?.value) {
                            price = product.price.sales.value;
                        }

                        const image = this.getImage(product);

                        this.viewController.commitCustomAttributes(placementCode, {
                            'fun-sku': product.id,
                            'fun-name': product.productName,
                            'fun-price': price ? price.toString() : '',
                            'fun-image': image,
                        });

                        this.$store.dispatch('setComponentQuantity', {
                            component: realProduct,
                            quantity: 1,
                        });

                        break;
                    }
                }
            },

            quantitySubtract(product) {
                if (product.quantity > 0) {
                    const selected = this.findSelected(product.id);

                    if (selected && selected.quantity > 0) {
                        const add = { ...selected, quantity: selected.quantity - 1 };

                        this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations.filter((x) => x.id !== product.id), add]);
                    }
                }
            },

            quantityAdd(product) {
                if (product.quantity < product.inventory) {
                    const selected = this.findSelected(product.id);

                    if (selected) {
                        const add = { ...selected, quantity: selected.quantity + 1 };

                        this.$store.dispatch('setSelectedRecommendations', [...this.selectedRecommendations.filter((x) => x.id !== product.id), add]);
                    }
                }
            },

            showProductInfoModal(product) {
                this.$store.dispatch('setProductInfoModalComponent', product);
                this.$store.dispatch('showProductInfoModal', true);
            },

            getProductDescription(name) {
                return this.$t('TOTAL_ADDED').replace('{0}', name);
            },

            findSelected(id) {
                if (this.selectedRecommendations == null) {
                    return null;
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const s of this.selectedRecommendations) {
                    if (s.id === id && s.quantity > 0) {
                        return s;
                    }
                }

                return null;
            },

            quantityLimit(ats) {
                const settings = this.$store.state.customizer.globalSettings;

                const recommendationLimit = +(settings.blueprint.custom['fun-recommendation-limit'] || '100');

                let limit = recommendationLimit;

                if (ats > 1 && ats < recommendationLimit) {
                    limit = ats;
                }

                return limit;
            },

            getImage(product) {
                let image = product?.images?.small[0]?.url;

                if (image == null) {
                    image = product?.images?.medium[0]?.url;
                }

                return image;
            },
        },
    };
</script>
